import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/cn';

interface Props {
  /** Text written to the clipboard on click. */
  value: string;
  /** Optional tooltip override; defaults to `Copy <value>`. */
  label?: string;
  className?: string;
}

/**
 * Tiny inline "copy to clipboard" affordance — used next to account /
 * content / persona ids in the chat panel and the persona accounts list.
 * Shows a transient check mark for ~1.5s after a successful copy.
 */
export function CopyButton({ value, label, className }: Props) {
  const [copied, setCopied] = useState(false);

  return (
    <button
      type="button"
      title={label ?? `Copy ${value}`}
      aria-label={label ?? `Copy ${value}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard
          ?.writeText(value)
          .then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          })
          .catch(() => {});
      }}
      className={cn(
        'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
        className,
      )}
    >
      {copied ? (
        <Check className="h-3 w-3 text-emerald-500" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </button>
  );
}
